import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { BadgeCheck, AlertCircle, Camera, Crop, User, Pencil, MapPin, Phone, Briefcase } from "lucide-react";
import { fetchProfile, updateProfile, uploadProfilePhoto, fetchLawyerPricing } from "@lawyer/services/lawyerApi";
import { listMyFinancialChanges } from "@lawyer/services/financialApi";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { AccountDangerZone } from "@/components/AccountDangerZone";
import PayoutKycCard from "@lawyer/components/PayoutKycCard";
import LevelCard from "@lawyer/components/LevelCard";
import { photoSrc } from "@shared/photoSrc";
import PhotoCropDialog from "@lawyer/components/PhotoCropDialog";
import { ProfileEditDialog } from "@lawyer/components/ProfileEditDialog";
import { specializationsOf } from "@shared/specializations";

function ProfilePhotoUpload({ photo, onUploaded }) {
  const inputRef = useRef(null);
  const [crop, setCrop] = useState(null);          // { source: Blob, mode: "new" | "reframe" }
  const [loadingOriginal, setLoadingOriginal] = useState(false);

  const pick = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const okType = ["image/jpeg", "image/png"].includes(file.type);
    if (!okType || file.size > 5 * 1024 * 1024) {
      toast.error("Please upload a JPG or PNG image smaller than 5 MB.");
      return;
    }
    setCrop({ source: file, mode: "new" });
  };

  // "Adjust framing" works on the stored original (never the sm/md variants).
  const reframe = async () => {
    if (!photo || loadingOriginal) return;
    setLoadingOriginal(true);
    try {
      const res = await fetch(photo.replace(/([?&])size=[^&]*&?/, "$1").replace(/[?&]$/, ""), { credentials: "include" });
      if (!res.ok) throw new Error("fetch");
      setCrop({ source: await res.blob(), mode: "reframe" });
    } catch {
      toast.error("Couldn't open your current photo — please try again.");
    }
    setLoadingOriginal(false);
  };

  // Called by the dialog with the exported JPEG; throwing keeps the dialog open so the lawyer can retry.
  const useCropped = async (blob) => {
    try {
      const res = await uploadProfilePhoto(new File([blob], "photo.jpg", { type: "image/jpeg" }));
      onUploaded(res.photo);
      setCrop(null);
      toast.success(crop?.mode === "reframe" ? "Framing updated" : "Profile photo updated");
    } catch (e) {
      const code = e?.response?.status;
      toast.error(code === 413 || code === 415
        ? "Please upload a JPG or PNG image smaller than 5 MB."
        : "Upload failed — your current photo is unchanged. Please try again.");
      throw e;
    }
  };

  const shown = photoSrc(photo, "md");
  return (
    <div>
      <Label className="text-xs">Profile photo</Label>
      <div className="mt-2 flex flex-wrap items-center gap-4">
        <div className="h-20 w-20 rounded-2xl border border-slate-200 bg-slate-50 overflow-hidden grid place-items-center shrink-0" data-testid="profile-photo-preview">
          {shown
            ? <img src={shown} alt="Profile" className="h-full w-full object-cover" />
            : <User size={28} className="text-slate-300" />}
        </div>
        <div className="min-w-0">
          <input ref={inputRef} type="file" accept="image/jpeg,image/png" className="hidden"
                 onChange={pick} data-testid="profile-photo-input" />
          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" onClick={() => inputRef.current?.click()} data-testid="profile-photo-upload-btn"
                    className="rounded-xl adv-btn-primary font-bold h-9 text-xs">
              <Camera size={13} /> {photo ? "Change photo" : "Upload photo"}
            </Button>
            {photo && (
              <button type="button" onClick={reframe} disabled={loadingOriginal} data-testid="profile-photo-reframe-btn"
                      className="h-9 px-3 rounded-xl border border-slate-200 text-xs font-bold text-slate-600 hover:bg-slate-50 inline-flex items-center gap-1.5 disabled:opacity-50">
                <Crop size={13} /> {loadingOriginal ? "Opening…" : "Adjust framing"}
              </button>
            )}
          </div>
          <p className="mt-2 text-[11px] text-slate-400">Professional headshot · JPG/PNG · max 5 MB · cropped to a square</p>
        </div>
      </div>
      <PhotoCropDialog source={crop?.source || null} mode={crop?.mode || "new"} onCancel={() => setCrop(null)} onUse={useCropped}
                       onDecodeError={() => { setCrop(null); toast.error("This image can't be opened — please choose a different JPG or PNG."); }} />
    </div>
  );
}

function Section({ title, hint, action, testid, children }) {
  return (
    <section className="bg-white border border-slate-200 rounded-2xl p-5" data-testid={testid}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-[11px] uppercase tracking-widest font-bold text-slate-400">{title}</h2>
          {hint && <p className="text-xs text-slate-500 mt-0.5">{hint}</p>}
        </div>
        {action}
      </div>
      <div className="mt-4">{children}</div>
    </section>
  );
}

const Fact = ({ icon: Icon, label, value, testid }) => (
  <div className="min-w-0">
    <p className="text-[11px] text-slate-400 inline-flex items-center gap-1">{Icon && <Icon size={11} />} {label}</p>
    <p className={`text-sm truncate ${value ? "font-semibold text-[#0A2540]" : "text-slate-400 italic"}`} data-testid={testid}>{value || "Not set"}</p>
  </div>
);

export default function LawyerProfile() {
  const [p, setP] = useState(null);
  const [editing, setEditing] = useState(false);
  const [prefBusy, setPrefBusy] = useState(false);

  // Secondary cards (pricing level, payout-detail change requests) are independent of the profile —
  // prefetched concurrently on first load; `undefined` = still in flight.
  const [pricing, setPricing] = useState(undefined);
  const [changeRequests, setChangeRequests] = useState(undefined);
  const load = async () => {
    const first = pricing === undefined;
    const secondary = first ? Promise.all([
      fetchLawyerPricing().catch(() => ({ error: true })),
      listMyFinancialChanges({ limit: 20 }).then((d) => (d ? d.items || [] : null)).catch(() => null),
    ]) : null;
    try { setP(await fetchProfile()); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not load profile"); }
    if (secondary) { const [pr, cr] = await secondary; setPricing(pr); setChangeRequests(cr); }
  };
  useEffect(() => { load(); }, []);

  // Same payload/endpoint as before — used by the edit dialog and by the WhatsApp preference toggle.
  const persist = async (next) => {
    const payload = {
      name: next.name,
      specialization: next.specialization,
      specializations: next.specializations || specializationsOf(next),
      ...(next.location?.city_id ? { location: { city_id: next.location.city_id } } : {}),
      languages: next.languages || [],
      experience: Number(next.experience || 0),
      bio: next.bio,
      photo: next.photo,
      phone: next.phone,
      whatsapp_opt_in: next.whatsapp_opt_in !== false,
    };
    try {
      const fresh = await updateProfile(payload);
      setP(fresh);
      toast.success("Profile saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); throw e; }
  };
  const toggleWhatsapp = async (v) => {
    setPrefBusy(true);
    try { await persist({ ...p, whatsapp_opt_in: v }); } catch { /* toast already shown; switch stays on the saved value */ }
    setPrefBusy(false);
  };

  if (!p) return <div className="text-slate-500" data-testid="lawyer-profile-loading">Loading…</div>;

  const city = p.location?.city_name || p.city || "";
  const cityLabel = city ? `${city}${p.location?.state_name ? `, ${p.location.state_name}` : ""}` : "";

  return (
    <>
      <div className="mb-6" data-testid="lawyer-profile-header">
        <h1 className="text-2xl sm:text-3xl font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>Profile</h1>
        <p className="mt-1 text-sm text-slate-500 inline-flex items-center gap-2">
          {p.verified
            ? <span className="inline-flex items-center gap-1 text-emerald-700"><BadgeCheck size={14} /> Verified by Advoset</span>
            : <span className="inline-flex items-center gap-1 text-amber-700"><AlertCircle size={14} /> Awaiting verification — clients won't see you in search yet</span>}
        </p>
      </div>

      <div className="grid gap-4 max-w-3xl xl:max-w-6xl xl:grid-cols-[minmax(0,1fr)_380px] items-start" data-testid="profile-layout">
        <div className="space-y-4 min-w-0">
          <Section title="Public profile" hint="What clients see on your listing." testid="profile-form"
                   action={(
                     <Button variant="outline" onClick={() => setEditing(true)} data-testid="profile-edit-btn" className="rounded-xl font-bold h-9 text-xs shrink-0">
                       <Pencil size={13} /> Edit
                     </Button>
                   )}>
            <div className="min-w-0">
              <p className="text-lg font-extrabold text-[#0A2540] truncate" style={{ fontFamily: "Poppins" }} data-testid="profile-view-name">{p.name || "—"}</p>
              <p className="text-sm text-slate-600" data-testid="profile-view-specialization">{specializationsOf(p).length ? specializationsOf(p).join(" · ") : <span className="italic text-slate-400">Specialization not set</span>}</p>
            </div>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Fact icon={MapPin} label="Practice location" value={cityLabel} testid="profile-view-city" />
              <Fact icon={Phone} label="Phone" value={p.phone} testid="profile-view-phone" />
              <Fact icon={Briefcase} label="Experience" value={p.experience != null && p.experience !== "" ? `${p.experience} year${Number(p.experience) === 1 ? "" : "s"}` : ""} testid="profile-view-experience" />
            </div>
            <div className="mt-4">
              <p className="text-[11px] text-slate-400">Languages</p>
              <div className="mt-1 flex flex-wrap gap-1.5" data-testid="profile-view-languages">
                {(p.languages || []).length
                  ? p.languages.map((l) => <span key={l} className="px-2.5 py-1 rounded-full text-xs font-bold bg-[#0A2540] text-[#D4AF37]">{l}</span>)
                  : <span className="text-sm text-slate-400 italic">Not set</span>}
              </div>
            </div>
            <div className="mt-4">
              <p className="text-[11px] text-slate-400">Bio</p>
              <p className={`mt-1 text-sm whitespace-pre-line ${p.bio ? "text-slate-700" : "text-slate-400 italic"}`} data-testid="profile-view-bio">{p.bio || "Not set"}</p>
            </div>
            <div className="mt-5 pt-4 border-t border-slate-100">
              <ProfilePhotoUpload photo={p.photo} onUploaded={(url) => setP((prev) => ({ ...prev, photo: url }))} />
            </div>
          </Section>

          <Section title="Preferences" testid="profile-preferences">
            <div className="flex items-center justify-between gap-4" data-testid="profile-whatsapp-row">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-[#0A2540]">WhatsApp updates</p>
                <p className="text-xs text-slate-500">Receive verification, KYC and payout updates on WhatsApp.</p>
              </div>
              <Switch checked={p.whatsapp_opt_in !== false} disabled={prefBusy} onCheckedChange={toggleWhatsapp} data-testid="profile-whatsapp-toggle" />
            </div>
          </Section>
        </div>

        <div className="space-y-4 min-w-0">
          {pricing !== undefined && <LevelCard initialData={pricing} />}

          {changeRequests !== undefined && <PayoutKycCard profile={p} initialRequests={changeRequests} />}

          <AccountDangerZone redirect="/lawyer-portal/login" className="" />
        </div>
      </div>

      <ProfileEditDialog open={editing} profile={p} onClose={() => setEditing(false)} onSave={persist} />
    </>
  );
}
